import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  setListingQueryParams,
  TListingQueryParams,
} from "../../api/store/commonSlice";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../components/modalEditListingColumns";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";

import { useGetBuzzerAlertListQuery } from "../../api/network/notificationApiServices";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { BuzzerAlert } from "../../api/types/Notifications";
import { TSelectboxOption } from "../../components/admin/formFields";
import HeaderView from "../../components/admin/headerView";
import AdminTable from "../../components/adminTable";
import ListingTableHeader from "../../components/listingTableHeader";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import { TAdminTableRowData } from "../../components/types";
import { APP_CONFIG } from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { useMapInit } from "../../hooks/mapInit";
import { useAccountTimezone } from "../../hooks/timezone";
import { routeUrls } from "../../navigation/routeUrls";
import { TMapState } from "../../types/map";
import { SortingStatusWithReset } from "../../utils/adminListings";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../utils/dateTime";
import { roundOffLatLong } from "../../utils/map";
import { useLoggedInUserData } from "../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";
import { getVehicleUUID } from "../map-fleet/common";
import BuzzerAlertStatusDropdown from "./components/buzzerAlertStatusDropdown";

const columnOrderingFields: Record<string, string> = {
  "Alert Type": "rule_description",
  "Asset ID": "device_id",
  "Asset Description": "device_description",
  "Display Name": "displayname",
  Driver: "drivername",
  "Date/Time": "timestamp",
  // "Address": "", // intentionally excluded from sortability
  // "Lat/Lng": "", // intentionally excluded from sortability
  Status: "status",
  // "Action": "", // intentionally excluded from sortability
};

const ScreenBuzzerAlerts = () => {
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const all_columns = [
    // "Sr No.",
    "Alert Type",
    "Asset ID",
    "Asset Description",
    "Display Name",
    ...(showDriver ? ["Driver"] : []),
    "Date/Time",
    "Address",
    "Lat/Lng",
    "Status",
    "Action",
  ];
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", { keyPrefix: "buzzerAlerts" });
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { buzzerAlerts: orgBuzzerAlertsQueryParams } = listingQueryParams;
  const buzzerAlertsQueryParamsRef = React.useRef(orgBuzzerAlertsQueryParams);
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);
  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );

  const { getUserACLAccessLevel } = useACL();
  const thisOrgPanicSOSAllowed = useLoggedInUserData("haspanicsos");
  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.BuzzerAlerts
  );
  const [columns, setColumns] = useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  useEffect(() => {
    buzzerAlertsQueryParamsRef.current = orgBuzzerAlertsQueryParams;
  }, [orgBuzzerAlertsQueryParams]);

  const updateBuzzerAlertListingQueryParams = (
    params: Partial<typeof orgBuzzerAlertsQueryParams>
  ) => {
    dispatch(
      setListingQueryParams({
        buzzerAlerts: {
          ...buzzerAlertsQueryParamsRef.current,
          ...params,
        },
      } as TListingQueryParams)
    );
  };

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    updateBuzzerAlertListingQueryParams({ page: 1, search: value });
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    updateBuzzerAlertListingQueryParams({
      ordering,
      page: 1,
    });
  };

  // fetching vehicle alert data
  const {
    data: dataOrgBuzzerAlerts,
    isFetching: isFetchingOrgBuzzerAlerts,
    error,
  } = useGetBuzzerAlertListQuery(
    {
      ...orgBuzzerAlertsQueryParams,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    },
    {
      pollingInterval: APP_CONFIG.API_POLLING_INTERVALS.NOTIFICATION_SUMMARY,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    }
  );
  const { count, next, previous, results } = dataOrgBuzzerAlerts || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgBuzzerAlerts]);

  // get user timezone
  const { tzForDateFns } = useAccountTimezone();

  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as BuzzerAlert[])).map(
        (item: BuzzerAlert, index: number) => {
          // Calculate reference date string in timezone for each row
          const refDateStringInTimezone = formattedDateTimeBasedOnTimezone(
            dbTimeToJsTime(item.event_info?.timestamp || 0), // Directly use item.event_info.timestamp
            accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
            "yyyy-MM-dd"
          );

          // Calculate start and end of day in UTC based on the refDateStringInTimezone
          const { startOfDayInUTC, endOfDayInUTC } = getStartEndOfDayInUTC(
            refDateStringInTimezone,
            accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE
          );

          const unixTimestampFrom = jsTimeToDBTime(startOfDayInUTC.getTime());
          const unixTimestampTo = jsTimeToDBTime(endOfDayInUTC.getTime());

          return {
            navLink:
              !!item.event_info?.latitude && item.event_info?.longitude
                ? routeUrls.dashboardChildren.mapsChildren.getVehicleMapUri(
                    getVehicleUUID({
                      accountid: accountData.accountid,
                      deviceid: item.device_id,
                      vehicleid: item.device_id,
                    }),
                    item.event_info?.timestamp || 0,
                    unixTimestampTo,
                    item.event_info?.timestamp || 0,
                    item.id
                  )
                : false,
            cellData: [
              //   index + 1 ?? '-', // "Sr No",
              item?.rule_description ?? "-", // "Alert Type",
              item?.device_id ?? "-", // "Asset ID",
              item?.device_description ?? "-", // "Asset Description",
              item?.displayname ?? "-", // "Asset Display name",
              ...(showDriver ? [item?.drivername ?? "-"] : []), // "Driver Name",
              !!item?.timestamp && !!tzForDateFns
                ? formattedDateTimeBasedOnTimezone(
                    item?.timestamp * 1000,
                    tzForDateFns
                  )
                : "-", // "Date/Time",
              item?.event_info?.address ?? "-", // "Address",
              // "Lat/Lng"
              <a
                className={`underline ${!!item.event_info?.latitude && !!item.event_info?.longitude ? "text-accent-blue-bright" : "text-gray-400 pointer-events-none underline-none"}`}
                href={APP_CONFIG.LAT_LNG_LINK(
                  item.event_info?.latitude || 0,
                  item.event_info?.longitude || 0
                )}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                {!!item?.event_info?.latitude && item?.event_info?.longitude
                  ? `${roundOffLatLong(item?.event_info.latitude || 0)},${roundOffLatLong(item?.event_info.longitude || 0)}`
                  : "-"}
              </a>,
              // "Alert Status"
              <BuzzerAlertStatusDropdown
                buzzerAlert={item}
                editPermission={
                  thisOrgPanicSOSAllowed === "1" &&
                  getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ALERTPANEL) >=
                    ROLE_ACCESS_LEVELS.WRITE
                }
              />,
              // "View"
              <button
                className="btn btn-primary enabled:text-accent-blue-dark disabled:text-gray-400 font-bold"
                type="button"
                // disabled={
                //   getUserACLAccessLevel(ACL_NAMES.ACL_MAP_ASSET) <
                //   ROLE_ACCESS_LEVELS.READ
                // }
                disabled={
                  !item.event_info?.latitude || !item.event_info?.longitude
                }
              >
                {t(
                  !!item.event_info?.latitude && !!item.event_info?.longitude
                    ? "view_map"
                    : "view_map_not_available"
                )}
              </button>,
            ].filter((_, index) => columns[index].show),
          };
        }
      )
    : [];

  // due to page components being dependent on map,
  // after 5 seconds of component mount, check if map script is loaded, load if not
  const { initMap } = useMapInit();
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!mapState?.mapScriptLoaded) {
        initMap();
      }
    }, 1000 * 5);
    return () => clearTimeout(timer);
  }, [mapState?.mapScriptLoaded]);

  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgBuzzerAlertsQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgBuzzerAlertsQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgBuzzerAlertsQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.BuzzerAlerts}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgBuzzerAlerts} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={
              isFetchingOrgBuzzerAlerts || mapState?.mapScriptLoaded !== true
            }
            listingQueryParams={orgBuzzerAlertsQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgBuzzerAlertsQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgBuzzerAlerts && (
            <Pagination
              pageSize={orgBuzzerAlertsQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                updateBuzzerAlertListingQueryParams({
                  page: 1,
                  page_size: parseInt(`${e?.value}`),
                });
              }}
              totalPages={
                count
                  ? Math.ceil(count / orgBuzzerAlertsQueryParams.page_size)
                  : 1
              }
              forcePage={orgBuzzerAlertsQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                updateBuzzerAlertListingQueryParams({
                  page: data?.selected + 1,
                });
              }}
              paginCurrentPage={orgBuzzerAlertsQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ScreenBuzzerAlerts;
